<template>
  <div>
    <label class="instructionsactivate">
      <input type="checkbox" v-model="handMouseActive" @change="toggleHandMouse" />
      <v-spacer></v-spacer>
      <span>Activate Hand Mouse</span>
    </label>
    <br />
    <div v-if="handMouseActive" class="instructions">
      <label>
        <input type="checkbox" v-model="useLeftHand" @change="toggleHandChoice" />
        <span>Click here to use <b>left hand</b></span>
      </label>
      <br />
      <p>
        <b>Pinch</b> your thumb and index finger to pick up a photo.
        <br />
        <b>Point</b> left or right to navigate back or forward.
        <br />
        <b>Fist</b> to click on a button or a link.
        <br />
        <b>Four fingers up</b> or <b>down</b> to scroll.
      </p>
      <br />
      <p>
        <b>Double tap</b> to click on a button or a link.
      </p>
      <br />
    </div>

    <div v-if="handMouseActive" class="debug_window">
      <video ref="inputVideo" class="input_video"></video>
      <canvas ref="outputCanvas" class="output_canvas" width="1920px" height="100%"></canvas>
    </div>

    <!-- <div class="movable photo photo_a">
      <div class="photo_frame">
        <img src="@/assets/cogneetiv-logo-2.png" alt="garden pond" />
      </div>
    </div> -->

    <div v-if="handMouseActive" ref="cursor" class="cursor wand"></div>

    <div v-if="notificationMessage" :class="['notification', notificationType]">
      {{ notificationMessage }}
    </div>

    <input type="checkbox" ref="autoActivateBtn" class="auto-activate-button" v-model="handMouseActive" @change="toggleHandMouse" />
  </div>
</template>

<script>
import { onMounted, ref, watch } from 'vue';
import { Hands, HAND_CONNECTIONS } from '@mediapipe/hands';
import { Camera } from '@mediapipe/camera_utils';
import { drawConnectors, drawLandmarks } from '@mediapipe/drawing_utils';

export default {
  name: 'HandMouse',
  setup() {
    const handMouseActive = ref(false);
    const useLeftHand = ref(false);
    const inputVideo = ref(null);
    const outputCanvas = ref(null);
    const cursor = ref(null);
    const notificationMessage = ref('');
    const notificationType = ref('');
    const autoActivateBtn = ref(null);

    const OPTIONS = {
      IS_DEBUG_MODE: false,
      PREFERRED_HAND: 'right',
      PINCH_DELAY_MS: 60,
    };

    const state = {
      isPinched: false,
      pinchChangeTimeout: null,
      grabbedElement: null,
      lastGrabbedElement: null,
    };

    const gestureState = {
      leftHoldStartTime: null,
      rightHoldStartTime: null,
    };

    const GESTURE_EVENTS = {
      PINCH_START: 'pinch_start',
      PINCH_MOVE: 'pinch_move',
      PINCH_STOP: 'pinch_stop',
      PICK_UP: 'pinch_pick_up',
      DROP: 'pinch_drop',
      FIST: 'fist',
      FOUR_FINGERS_UP: 'four_fingers_up',
      FOUR_FINGERS_DOWN: 'four_fingers_down',
      POINT_LEFT: 'point_left',
      POINT_RIGHT: 'point_right',
    };

    const handParts = {
      wrist: 0,
      thumb: { base: 1, middle: 2, topKnuckle: 3, tip: 4 },
      indexFinger: { base: 5, middle: 6, topKnuckle: 7, tip: 8 },
      middleFinger: { base: 9, middle: 10, topKnuckle: 11, tip: 12 },
      ringFinger: { base: 13, middle: 14, topKnuckle: 15, tip: 16 },
      pinky: { base: 17, middle: 18, topKnuckle: 19, tip: 20 },
    };

    function toggleHandChoice() {
      OPTIONS.PREFERRED_HAND = useLeftHand.value ? 'left' : 'right';
    }

    function toggleHandMouse() {
      if (handMouseActive.value) {
        activateHandMouse();
      } else {
        deactivateHandMouse();
      }
    }

    function activateHandMouse() {
      try {
        setupHandTracking();
        showNotification('Hand mouse activated successfully.', 'success');
      } catch (error) {
        showNotification('Error activating hand mouse.', 'error');
      }
    }

    function deactivateHandMouse() {
      try {
        stopHandTracking();
        showNotification('Hand mouse deactivated successfully.', 'success');
      } catch (error) {
        showNotification('Error deactivating hand mouse.', 'error');
      }
    }

    function showNotification(message, type) {
      notificationMessage.value = message;
      notificationType.value = type;
      setTimeout(() => {
        notificationMessage.value = '';
        notificationType.value = '';
      }, 3000);
    }

    function setupHandTracking() {
      if (!inputVideo.value) {
        showNotification('Error: Video element not found.', 'error');
        return;
      }

      const hands = new Hands({ locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}` });
      hands.setOptions({
        maxNumHands: 1,
        modelComplexity: 1,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      hands.onResults(onResults);

      const camera = new Camera(inputVideo.value, {
        onFrame: async () => {
          await hands.send({ image: inputVideo.value });
        },
        width: 1920,
        height: 1080,
      });
      camera.start();

      document.addEventListener(GESTURE_EVENTS.PINCH_START, onPinchStart);
      document.addEventListener(GESTURE_EVENTS.PINCH_MOVE, onPinchMove);
      document.addEventListener(GESTURE_EVENTS.PINCH_STOP, onPinchStop);
      document.addEventListener(GESTURE_EVENTS.PICK_UP, onPickUp);
      document.addEventListener(GESTURE_EVENTS.DROP, onDrop);

      setupDoubleTapDetection();
    }

    function stopHandTracking() {
      // Add logic to stop the hand tracking process
    }

    function triggerEvent({ eventName, eventData }) {
      const event = new CustomEvent(eventName, { detail: eventData });
      document.dispatchEvent(event);
    }

    function getElementCoords(element) {
      const rect = element.getBoundingClientRect();
      const elementTop = rect.top / window.innerHeight;
      const elementBottom = rect.bottom / window.innerHeight;
      const elementLeft = rect.left / window.innerWidth;
      const elementRight = rect.right / window.innerWidth;
      return { elementTop, elementBottom, elementLeft, elementRight };
    }

    function isElementPinched({ pinchX, pinchY, elementTop, elementBottom, elementLeft, elementRight }) {
      const isPinchInXRange = elementLeft <= pinchX && pinchX <= elementRight;
      const isPinchInYRange = elementTop <= pinchY && pinchY <= elementBottom;
      return isPinchInXRange && isPinchInYRange;
    }

    function getPinchedElement({ pinchX, pinchY, elements }) {
      let grabbedElement;
      for (const element of elements) {
        const elementCoords = getElementCoords(element);
        if (isElementPinched({ pinchX, pinchY, ...elementCoords })) {
          grabbedElement = {
            domNode: element,
            coords: {
              x: elementCoords.elementLeft,
              y: elementCoords.elementTop,
            },
            offsetFromCorner: {
              x: pinchX - elementCoords.elementLeft,
              y: pinchY - elementCoords.elementTop,
            },
          };
          const isTopElement = element === state.lastGrabbedElement;
          if (isTopElement) {
            return grabbedElement;
          }
        }
      }
      return grabbedElement;
    }

    function log(...args) {
      if (OPTIONS.IS_DEBUG_MODE) {
        console.log(...args);
      }
    }

    function getCurrentHand(handData) {
      const isHandAvailable = !!handData.multiHandLandmarks?.[0];
      if (!isHandAvailable) {
        return null;
      }
      const mirroredHand = handData.multiHandedness[0].label === 'Left' ? 'right' : 'left';
      return mirroredHand;
    }

    function isPinched(handData) {
      if (isPrimaryHandAvailable(handData)) {
        const fingerTip = handData.multiHandLandmarks[0][handParts.indexFinger.tip];
        const thumbTip = handData.multiHandLandmarks[0][handParts.thumb.tip];
        const distance = {
          x: Math.abs(fingerTip.x - thumbTip.x),
          y: Math.abs(fingerTip.y - thumbTip.y),
          z: Math.abs(fingerTip.z - thumbTip.z),
        };
        const areFingersCloseEnough = distance.x < 0.08 && distance.y < 0.08 && distance.z < 0.11;
        log(distance);
        log({ isPinched: areFingersCloseEnough });
        return areFingersCloseEnough;
      }
      return false;
    }

    function isFist(handData) {
      if (isPrimaryHandAvailable(handData)) {
        const fistThreshold = 0.1;
        const distanceBetweenFingers = handParts.indexFinger.tip - handParts.wrist;
        return distanceBetweenFingers < fistThreshold;
      }
      return false;
    }

    function isFourFingersUp(handData) {
      if (isPrimaryHandAvailable(handData)) {
        const indexFinger = handData.multiHandLandmarks[0][handParts.indexFinger.tip];
        const middleFinger = handData.multiHandLandmarks[0][handParts.middleFinger.tip];
        const ringFinger = handData.multiHandLandmarks[0][handParts.ringFinger.tip];
        const pinky = handData.multiHandLandmarks[0][handParts.pinky.tip];
        const wrist = handData.multiHandLandmarks[0][handParts.wrist];

        return (
          indexFinger.y < wrist.y &&
          middleFinger.y < wrist.y &&
          ringFinger.y < wrist.y &&
          pinky.y < wrist.y &&
          Math.abs(indexFinger.y - middleFinger.y) < 0.1 &&
          Math.abs(middleFinger.y - ringFinger.y) < 0.1 &&
          Math.abs(ringFinger.y - pinky.y) < 0.1
        );
      }
      return false;
    }

    function isFourFingersDown(handData) {
      if (isPrimaryHandAvailable(handData)) {
        const indexFinger = handData.multiHandLandmarks[0][handParts.indexFinger.tip];
        const middleFinger = handData.multiHandLandmarks[0][handParts.middleFinger.tip];
        const ringFinger = handData.multiHandLandmarks[0][handParts.ringFinger.tip];
        const pinky = handData.multiHandLandmarks[0][handParts.pinky.tip];
        const wrist = handData.multiHandLandmarks[0][handParts.wrist];

        return (
          indexFinger.y > wrist.y &&
          middleFinger.y > wrist.y &&
          ringFinger.y > wrist.y &&
          pinky.y > wrist.y &&
          Math.abs(indexFinger.y - middleFinger.y) < 0.1 &&
          Math.abs(middleFinger.y - ringFinger.y) < 0.1 &&
          Math.abs(ringFinger.y - pinky.y) < 0.1
        );
      }
      return false;
    }

    function isPointingLeft(handData) {
      if (isPrimaryHandAvailable(handData)) {
        const indexFinger = handData.multiHandLandmarks[0][handParts.indexFinger.tip];
        const wrist = handData.multiHandLandmarks[0][handParts.wrist];
        return indexFinger.x < wrist.x - 0.2;
      }
      return false;
    }

    function isPointingRight(handData) {
      if (isPrimaryHandAvailable(handData)) {
        const indexFinger = handData.multiHandLandmarks[0][handParts.indexFinger.tip];
        const wrist = handData.multiHandLandmarks[0][handParts.wrist];
        return indexFinger.x > wrist.x + 0.2;
      }
      return false;
    }

    function convertCoordsToDomPosition({ x, y }) {
      return {
        x: `${x * 100}vw`,
        y: `${y * 100}vh`,
      };
    }

    function updateDebugCanvas(handData) {
      const debugCanvasCtx = outputCanvas.value.getContext('2d');
      debugCanvasCtx.save();
      debugCanvasCtx.clearRect(0, 0, outputCanvas.value.width, outputCanvas.value.height);
      debugCanvasCtx.drawImage(handData.image, 0, 0, outputCanvas.value.width, outputCanvas.value.height);
      if (handData.multiHandLandmarks) {
        for (const landmarks of handData.multiHandLandmarks) {
          drawConnectors(debugCanvasCtx, landmarks, HAND_CONNECTIONS, { color: '#0ff', lineWidth: 5 });
          drawLandmarks(debugCanvasCtx, landmarks, { color: '#f0f', lineWidth: 2 });
        }
      }
      debugCanvasCtx.restore();
    }

    function getCursorCoords(handData) {
      const { x, y, z } = handData.multiHandLandmarks[0][handParts.indexFinger.middle];
      const mirroredXCoord = -x + 1; /* due to camera mirroring */
      return { x: mirroredXCoord, y, z };
    }

    function isPrimaryHandAvailable(handData) {
      return getCurrentHand(handData) === OPTIONS.PREFERRED_HAND;
    }

    function onResults(handData) {
      if (!handData) {
        return;
      }
      if (OPTIONS.IS_DEBUG_MODE) {
        updateDebugCanvas(handData);
      }

      updateCursor(handData);
      updatePinchState(handData);
      updateFistState(handData);
      updateFourFingersScrollState(handData);
      updatePointingState(handData);
    }

    function updateCursor(handData) {
      if (isPrimaryHandAvailable(handData)) {
        const cursorCoords = getCursorCoords(handData);
        if (!cursorCoords) {
          return;
        }
        const { x, y } = convertCoordsToDomPosition(cursorCoords);
        cursor.value.style.transform = `translate(${x}, ${y})`;
      }
    }

    function updatePinchState(handData) {
      const wasPinchedBefore = state.isPinched;
      const isPinchedNow = isPinched(handData);
      const hasPassedPinchThreshold = isPinchedNow !== wasPinchedBefore;
      const hasWaitStarted = !!state.pinchChangeTimeout;

      if (hasPassedPinchThreshold && !hasWaitStarted) {
        registerChangeAfterWait(handData, isPinchedNow);
      }

      if (!hasPassedPinchThreshold) {
        cancelWaitForChange();
        if (isPinchedNow) {
          triggerEvent({
            eventName: GESTURE_EVENTS.PINCH_MOVE,
            eventData: getCursorCoords(handData),
          });
        }
      }
    }

    function registerChangeAfterWait(handData, isPinchedNow) {
      state.pinchChangeTimeout = setTimeout(() => {
        state.isPinched = isPinchedNow;
        triggerEvent({
          eventName: isPinchedNow ? GESTURE_EVENTS.PINCH_START : GESTURE_EVENTS.PINCH_STOP,
          eventData: getCursorCoords(handData),
        });
      }, OPTIONS.PINCH_DELAY_MS);
    }

    function cancelWaitForChange() {
      clearTimeout(state.pinchChangeTimeout);
      state.pinchChangeTimeout = null;
    }

    function updateFistState(handData) {
      if (isFist(handData)) {
        triggerEvent({
          eventName: GESTURE_EVENTS.FIST,
          eventData: getCursorCoords(handData),
        });
      }
    }

    function updateFourFingersScrollState(handData) {
      if (isFourFingersUp(handData)) {
        triggerEvent({
          eventName: GESTURE_EVENTS.FOUR_FINGERS_UP,
          eventData: getCursorCoords(handData),
        });
      } else if (isFourFingersDown(handData)) {
        triggerEvent({
          eventName: GESTURE_EVENTS.FOUR_FINGERS_DOWN,
          eventData: getCursorCoords(handData),
        });
      }
    }

    function updatePointingState(handData) {
      const now = new Date().getTime();

      if (isPointingLeft(handData)) {
        if (!gestureState.leftHoldStartTime) {
          gestureState.leftHoldStartTime = now;
        } else if (now - gestureState.leftHoldStartTime >= 1500) {
          window.history.back();
          gestureState.leftHoldStartTime = null;
        }
      } else {
        gestureState.leftHoldStartTime = null;
      }

      if (isPointingRight(handData)) {
        if (!gestureState.rightHoldStartTime) {
          gestureState.rightHoldStartTime = now;
        } else if (now - gestureState.rightHoldStartTime >= 1500) {
          window.history.forward();
          gestureState.rightHoldStartTime = null;
        }
      } else {
        gestureState.rightHoldStartTime = null;
      }
    }

    function onPinchStart(eventInfo) {
      const cursorCoords = eventInfo.detail;

      state.grabbedElement = getPinchedElement({
        pinchX: cursorCoords.x,
        pinchY: cursorCoords.y,
        elements: document.querySelectorAll('.movable'),
      });
      if (state.grabbedElement) {
        triggerEvent({
          eventName: GESTURE_EVENTS.PICK_UP,
          eventData: state.grabbedElement.domNode,
        });
      }

      document.body.classList.add('is-pinched');
    }

    function onPinchMove(eventInfo) {
      const cursorCoords = eventInfo.detail;

      if (state.grabbedElement) {
        state.grabbedElement.coords = {
          x: cursorCoords.x - state.grabbedElement.offsetFromCorner.x,
          y: cursorCoords.y - state.grabbedElement.offsetFromCorner.y,
        };

        const { x, y } = convertCoordsToDomPosition(state.grabbedElement.coords);
        state.grabbedElement.domNode.style.transform = `translate(${x}, ${y})`;
      }
    }

    function onPinchStop() {
      document.body.classList.remove('is-pinched');
      if (state.grabbedElement) {
        triggerEvent({
          eventName: GESTURE_EVENTS.DROP,
          eventData: state.grabbedElement.domNode,
        });
      }
    }

    function onPickUp(eventInfo) {
      const element = eventInfo.detail;
      state.lastGrabbedElement?.style.removeProperty('z-index');
      state.lastGrabbedElement = element;
      element.style.zIndex = 1;
      element.classList.add('element_dragging');
    }

    function onDrop(eventInfo) {
      const element = eventInfo.detail;
      state.isDragging = false;
      state.grabbedElement = undefined;
      element.classList.remove('element_dragging');
    }

    function setupDoubleTapDetection() {
      let lastTapTime = 0;

      document.addEventListener('pinch_move', (event) => {
        const currentTime = new Date().getTime();
        const tapLength = currentTime - lastTapTime;
        if (tapLength < 500 && tapLength > 0) {
          triggerEvent({
            eventName: 'double_tap',
            eventData: event.detail,
          });
        }
        lastTapTime = currentTime;
      });

      document.addEventListener('double_tap', (event) => {
        const targetElement = document.elementFromPoint(event.detail.x * window.innerWidth, event.detail.y * window.innerHeight);
        if (targetElement) {
          if (targetElement.tagName.toLowerCase() === 'a' || targetElement.tagName.toLowerCase() === 'button') {
            targetElement.click();
          }
        }
      });

      document.addEventListener(GESTURE_EVENTS.FIST, (event) => {
        const targetElement = document.elementFromPoint(event.detail.x * window.innerWidth, event.detail.y * window.innerHeight);
        if (targetElement) {
          if (targetElement.tagName.toLowerCase() === 'a' || targetElement.tagName.toLowerCase() === 'button') {
            targetElement.click();
          }
        }
      });

      document.addEventListener(GESTURE_EVENTS.FOUR_FINGERS_UP, () => {
        window.scrollBy(0, -100);
      });

      document.addEventListener(GESTURE_EVENTS.FOUR_FINGERS_DOWN, () => {
        window.scrollBy(0, 100);
      });
    }

    function autoActivate() {
      setTimeout(() => {
        handMouseActive.value = true;
      }, 5000);
    }

    function handleVoiceCommand(command) {
      if (command === 'hand mouse on') {
        handMouseActive.value = true;
      } else if (command === 'hand mouse off') {
        handMouseActive.value = false;
      }
    }

    onMounted(() => {
      watch(handMouseActive, (newVal) => {
        if (newVal) {
          activateHandMouse();
        } else {
          deactivateHandMouse();
        }
      });

      // Setup voice command handling (this is a placeholder, actual implementation depends on the voice recognition library used)
      if (window.SpeechRecognition) {
        const recognition = new window.SpeechRecognition();
        recognition.continuous = true;
        recognition.onresult = (event) => {
          const transcript = event.results[event.results.length - 1][0].transcript.trim();
          handleVoiceCommand(transcript.toLowerCase());
        };
        recognition.start();
      }

      autoActivate();
    });

    return {
      handMouseActive,
      useLeftHand,
      inputVideo,
      outputCanvas,
      cursor,
      notificationMessage,
      notificationType,
      toggleHandChoice,
      toggleHandMouse,
      autoActivateBtn,
    };
  },
};
</script>

<style scoped>
.auto-activate-button {
  display: none;
  color: #ffffff;
  background-color: #150f1b;
}
.instructionsactivate {
  color: #ffffff;
  background-color: #150f1b;
  font-size: 16px;
  left: 10px;
  padding: 20px;
  line-height: 1.4;
  font-family: verdana;
  margin-top: -22px;
}
.instructions {
  color: #ffffff;
  background-color: #150f1b;
  font-size: 20px;
  padding: 20px;
  line-height: 1.4;
  font-family: verdana;
  margin-top: -22px;
}
.instructions b {
  color: gold;
}
.instructions label {
  cursor: pointer;
}
.instructions label span:hover {
  text-decoration: underline;
}

.movable {
  position: absolute;
  left: 0;
  top: 0;
  transition: transform 0.2s;
}

body {
  margin: 0;
  font-family: sans-serif;
  background-color: #333;
}

.input_video {
  display: none;
}
.output_canvas {
  display: none;
  width: 400px;
}

.wand {
  height: 0;
  width: 0;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 10;
  transition: transform 0.1s;
}

.wand::after {
  content: '';
  display: block;
  height: 50px;
  width: 50px;
  border-radius: 50%;
  position: absolute;
  left: 0;
  top: 0;
  transform: translate(-50%, -50%);
  transition: transform 0.2s;
  box-shadow: inset 0 0 4px 2px white, inset 0 0 4px #0098db, 0 0 8px #fff, -4px 0 10px #f0fd, 4px 0 10px #0ffd;
  animation: rotate-cursor 5s infinite linear;
}

@keyframes rotate-cursor {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.photo_frame {
  width: 200px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 3px;
  background: #ffffff00;
  transition: transform 0.2s;
  box-shadow: 0 2px 10px 0 #000e;
}
.element_dragging .photo_frame {
  box-shadow: 0 10px 20px 5px #000c;
  transform: scale(1.2);
}
.photo img {
  width: 70%;
  display: block;
  align-content: center;
  margin: 0 auto;
}
.photo_a {
  transform: translate(5vw, 20vh);
}
.photo_b {
  transform: translate(55vw, 50vh);
}
.photo_a .photo_frame {
  width: 200px;
}

.notification {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 15px;
  border-radius: 5px;
  font-size: 16px;
  color: #fff;
  z-index: 1000;
}
.notification.success {
  background-color: #4caf50;
}
.notification.error {
  background-color: #f44336;
}
</style>
